import type { CategoryDTO, ChannelDTO } from './types';

/** Anything placed in the sidebar: a channel or a category (as sent to the app, or a database row). */
type Placed = { id: string; position: number; createdAt?: string | Date };
type PlacedChannel = Placed & { categoryId: string | null };

export interface SidebarGroup<C = ChannelDTO, K = CategoryDTO> {
  category: K | null;
  channels: C[];
}

const time = (v: string | Date | undefined) => (v instanceof Date ? v.toISOString() : (v ?? ''));
const byPosition = (a: Placed, b: Placed) => a.position - b.position || time(a.createdAt).localeCompare(time(b.createdAt)) || a.id.localeCompare(b.id);

/**
 * The server sidebar layout: channels without a category first, then each
 * category (by position) with its channels. Used by the sidebar, keyboard
 * navigation and the server (which channel gets server-wide notes), so all agree.
 */
export function sidebarGroups<C extends PlacedChannel, K extends Placed>(channels: C[], categories: K[]): SidebarGroup<C, K>[] {
  const known = new Set(categories.map((c) => c.id));
  const uncategorized = channels.filter((c) => !c.categoryId || !known.has(c.categoryId)).sort(byPosition);
  const groups: SidebarGroup<C, K>[] = uncategorized.length ? [{ category: null, channels: uncategorized }] : [];
  for (const category of [...categories].sort(byPosition)) {
    groups.push({ category, channels: channels.filter((c) => c.categoryId === category.id).sort(byPosition) });
  }
  return groups;
}

/** Channels flattened in the exact order they appear in the sidebar. */
export function sidebarChannelOrder<C extends PlacedChannel>(channels: C[], categories: Placed[]): C[] {
  return sidebarGroups(channels, categories).flatMap((g) => g.channels);
}
